import { createContext } from 'react'

import { IProductCard } from '@src/components/product-card/product-card.types'
import { EMPTY_ARROW_FUNC } from '@src/config/constants'

import { ProductPageExpValue, SimilarProductExpValue } from '../templates/productPage/productPage.utils'

import { RECOMMENDED_SECTION_IDS } from './productPageContext.constants'

export interface RecommendationModuleData {
  module_id: string
  module_name: string
  module_title: string
  module_title_key: string
  slot_id: string
  data: IProductCard[]
  has_reached_limit?: boolean
}

export interface RecommendationUIData {
  [RECOMMENDED_SECTION_IDS.SLOT1]: RecommendationModuleData
  [RECOMMENDED_SECTION_IDS.SLOT2]: RecommendationModuleData
  [RECOMMENDED_SECTION_IDS.SLOT3]: RecommendationModuleData
  [RECOMMENDED_SECTION_IDS.SLOT4]: RecommendationModuleData
}

type ProductPageContextType =
  | {
      recommendationUIData: RecommendationUIData
      isRecommendationLoading: boolean
      updateRecommendationAPIData: (sectionKey: RECOMMENDED_SECTION_IDS, sectionValue: RecommendationModuleData) => void
      hasMoreToExplore: boolean
      setHasMoreToExplore: (val: boolean) => void
      productPageExpData: ProductPageExpValue
      showFullPDP: boolean
      setShowFullPDP: (val: boolean) => void
      similarProductExpData: SimilarProductExpValue
      newPDPRecommendations: boolean
      isPdpColorOrderReady: boolean
    }
  | undefined

const similarProductDefaultData = {
  title: '',
  titleClassname: '',
  showInLineMiniProduct: false,
  productCardExpClassName: '',
  similarProductsWrapperClassname: '',
  chunkSize: 4,
  infiniteScroll: false,
  showDots: true,
  wishlistIconOverrideClass: '',
  swiperClassName: '',
  hideWishlistIcon: false,
  hideProductName: false,
  gridColCount: 4,
  isVertical: false,
  productPriceClass: '',
  productCardLinkClass: '',
  hideTags: false,
  productPriceWrapperClass: '',
}

export const productContextInitialState = {
  recommendationUIData: {
    /* eslint-disable i18next/no-literal-string */
    /**
     * module_title_key is used for rendering the title in the ui
     * the rest are used for analytics etc hence we can disable i18n eslint rule
     */
    [RECOMMENDED_SECTION_IDS.SLOT1]: {
      module_id: '1001',
      module_name: 'COMPLETE THE LOOK',
      module_title: 'Complete the Look',
      module_title_key: 'complete_the_look',
      slot_id: 'slot_1',
      data: [],
    },
    [RECOMMENDED_SECTION_IDS.SLOT2]: {
      module_id: '1002',
      module_name: 'More to explore',
      module_title: 'More to explore',
      module_title_key: 'more_to_explore',
      slot_id: 'slot_2',
      data: [],
    },
    [RECOMMENDED_SECTION_IDS.SLOT3]: {
      module_id: '1003',
      module_name: 'Quince shoppers also love',
      module_title: 'Quince shoppers also love',
      module_title_key: 'quince_shoppers_also_love',
      slot_id: 'slot_3',
      data: [],
    },
    [RECOMMENDED_SECTION_IDS.SLOT4]: {
      module_id: '1004',
      module_name: 'Based on your taste',
      module_title: 'Based on your taste',
      module_title_key: 'based_on_your_taste',
      slot_id: 'slot_4',
      data: [],
    },
    /* eslint-enable i18next/no-literal-string */
  } as RecommendationUIData,
  isRecommendationLoading: false,
  updateRecommendationAPIData: EMPTY_ARROW_FUNC,
  hasMoreToExplore: false,
  setHasMoreToExplore: EMPTY_ARROW_FUNC,
  productPageExpData: {
    showSmallProductImage: false,
    productImageClass: '',
    productImageLeftContainerClass: '',
    productImageLeftContainerSliderClass: '',
    hideProductThumbnails: false,
    productFormWrapperClass: '',
    productFormOptionsClass: '',
    moveAddToCartBtnToRight: false,
    imageContainerClassName: '',
    showViewFullProductDetails: false,
    hideProductDetail: false,
    hideAddToWishlist: false,
    hideReviewCount: false,
    viewFullProductText: '',
    productInfoHeaderTextClassName: '',
    hideBreadCrumb: false,
    hideProductDescFooter: false,
    hideReviewLabel: false,
    sliderIconClassName: '',
    breadCrumbListClass: '',
    productInfoReviewClassName: '',
    inStockMsgClass: '',
    productImageRightContainerClass: '',
    breadCrumbContainerHeaderClass: '',
    sliderImageClass: '',
    showReadMoreInDesc: false,
    showInlineSizeChart: false,
    hideSizeChart: false,
  },
  showFullPDP: false,
  setShowFullPDP: EMPTY_ARROW_FUNC,
  similarProductExpData: similarProductDefaultData,
  newPDPRecommendations: false,
  isPdpColorOrderReady: true,
}

const ProductPageContext = createContext<ProductPageContextType>(undefined)

export default ProductPageContext
